import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import {
  ClipboardList, Truck, Building2, CalendarIcon, Check, Plus, Trash2, Package,
} from 'lucide-react';
import { useCustomers, useSites, useCategories } from '@/hooks/useSettings';
import { useCreateWorkOrder } from '@/hooks/useWorkOrders';
import type { WorkOrderType, OwnershipModel, WorkOrderEstimate } from '@/types/models';

export default function WorkOrderCreatePage() {
  const navigate = useNavigate();
  const { data: customers = [] } = useCustomers();
  const { data: sites = [] } = useSites();
  const { data: categories = [] } = useCategories();
  const createWO = useCreateWorkOrder();

  // Form state
  const [customerId, setCustomerId] = useState('');
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [woType, setWoType] = useState<WorkOrderType | ''>('');
  const [siteId, setSiteId] = useState('');
  const [scheduledDate, setScheduledDate] = useState<Date>();
  const [assignedOperator, setAssignedOperator] = useState('');
  const [ownershipModel, setOwnershipModel] = useState<OwnershipModel>('recycler_owned');
  const [notes, setNotes] = useState('');

  // Pickup fields
  const [pickupAddress, setPickupAddress] = useState('');
  const [driver, setDriver] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [estimates, setEstimates] = useState<{ categoryId: string; measureType: 'qty' | 'lbs'; value: number }[]>([]);

  const selectedCustomer = customers.find((c) => c.id === customerId);

  function handleCustomerChange(id: string) {
    setCustomerId(id);
    const cust = customers.find((c) => c.id === id);
    if (cust) {
      setContactName('');
      setPhone('');
      setEmail('');
    }
  }

  function addEstimateLine() {
    setEstimates((prev) => [...prev, { categoryId: '', measureType: 'qty', value: 0 }]);
  }

  function removeEstimateLine(index: number) {
    setEstimates((prev) => prev.filter((_, i) => i !== index));
  }

  function updateEstimate(index: number, field: 'categoryId' | 'measureType' | 'value', val: string | number) {
    setEstimates((prev) =>
      prev.map((e, i) => (i === index ? { ...e, [field]: val } : e))
    );
  }

  function handleSubmit() {
    if (!customerId || !woType || !siteId || !scheduledDate) return;

    createWO.mutate(
      {
        customer_id: customerId,
        customer_name: selectedCustomer?.name || selectedCustomer?.company || '',
        company: selectedCustomer?.name || selectedCustomer?.company || '',
        phone,
        email,
        type: woType,
        work_order_type: woType,
        ownership_model: ownershipModel,
        status: 'scheduled',
        site_id: siteId,
        scheduled_date: format(scheduledDate, 'yyyy-MM-dd'),
        assigned_operator: assignedOperator || undefined,
        notes: notes || undefined,
        driver: woType === 'pickup' ? driver || undefined : undefined,
        vehicle: woType === 'pickup' ? vehicle || undefined : undefined,
        pickup_address: woType === 'pickup' ? pickupAddress || undefined : undefined,
      },
      {
        onSuccess: (wo) => navigate(`/work-orders/${wo.id}`),
      }
    );
  }

  const isValid = customerId && woType && siteId && scheduledDate;

  return (
    <>
      <PageHeader title="New Work Order" description="Create a pickup or drop-off work order" icon={ClipboardList} />

      <div className="max-w-3xl space-y-6">
        {/* Type & Ownership */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <Label className="mb-3 block text-sm font-semibold">Work Order Type</Label>
            <div className="grid grid-cols-2 gap-4">
              {([
                { value: 'pickup' as const, label: 'Pickup', desc: 'Send a driver to collect assets from the customer site', icon: Truck },
                { value: 'dropoff' as const, label: 'Drop-off', desc: 'Customer delivers assets to your facility', icon: Building2 },
              ]).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setWoType(opt.value)}
                  className={cn(
                    'relative flex flex-col items-center gap-3 rounded-lg border-2 p-6 text-center transition-all',
                    woType === opt.value
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-card hover:border-muted-foreground/30'
                  )}
                >
                  {woType === opt.value && (
                    <div className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-primary">
                      <Check className="h-4 w-4 text-primary-foreground" />
                    </div>
                  )}
                  <opt.icon className={cn('h-8 w-8', woType === opt.value ? 'text-primary' : 'text-muted-foreground')} />
                  <div>
                    <p className="text-sm font-semibold text-foreground">{opt.label}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{opt.desc}</p>
                  </div>
                </button>
              ))}
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Ownership Model</Label>
              <Select value={ownershipModel} onValueChange={(v) => setOwnershipModel(v as OwnershipModel)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="recycler_owned">Recycler Owned</SelectItem>
                  <SelectItem value="customer_owned">Customer Owned</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Customer & Contact */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <Label className="text-sm font-semibold">Customer</Label>
            <Select value={customerId} onValueChange={handleCustomerChange}>
              <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name || c.company || '(unnamed)'}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {customerId && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Contact</Label>
                  <Input value={contactName} onChange={(e) => setContactName(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Phone</Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Site, Date, Operator */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <Label className="text-xs text-muted-foreground">Site</Label>
                <Select value={siteId} onValueChange={setSiteId}>
                  <SelectTrigger><SelectValue placeholder="Select site" /></SelectTrigger>
                  <SelectContent>
                    {sites.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Scheduled Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn('w-full justify-start text-left font-normal', !scheduledDate && 'text-muted-foreground')}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {scheduledDate ? format(scheduledDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={scheduledDate} onSelect={setScheduledDate} initialFocus className="p-3 pointer-events-auto" />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Assigned Operator</Label>
                <Input value={assignedOperator} onChange={(e) => setAssignedOperator(e.target.value)} placeholder="Optional" />
              </div>
            </div>
          </CardContent>
        </Card>


        {/* Pickup-specific fields */}
        {woType === 'pickup' && (
          <Card>
            <CardContent className="p-6 space-y-4">
              <Label className="text-sm font-semibold">Pickup Details</Label>
              <div>
                <Label className="text-xs text-muted-foreground">Pickup Address</Label>
                <Input value={pickupAddress} onChange={(e) => setPickupAddress(e.target.value)} placeholder="123 Main St, City, ST" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Driver</Label>
                  <Input value={driver} onChange={(e) => setDriver(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Vehicle</Label>
                  <Input value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="Truck #" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pre-Intake Category Estimates */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Pre-Intake Category Estimates</Label>
              <Button type="button" variant="outline" size="sm" onClick={addEstimateLine} className="gap-1.5">
                <Plus className="h-3.5 w-3.5" /> Add Line
              </Button>
            </div>
            {estimates.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No estimate lines yet. Click "Add Line" to begin.</p>
            ) : (
              estimates.map((est, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Select value={est.categoryId} onValueChange={(v) => updateEstimate(i, 'categoryId', v)}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Category" /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    className="w-24"
                    placeholder={est.measureType === 'qty' ? 'Qty' : 'Lbs'}
                    value={est.value || ''}
                    onChange={(e) => updateEstimate(i, 'value', parseFloat(e.target.value) || 0)}
                  />
                  <Select value={est.measureType} onValueChange={(v) => updateEstimate(i, 'measureType', v)}>
                    <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="qty">Qty</SelectItem>
                      <SelectItem value="lbs">Lbs</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeEstimateLine(i)}>
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Drop-off notice */}
        {woType === 'dropoff' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start gap-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
                <Package className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Drop-off Intake</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    When the customer arrives, start the intake process from the work order detail page. A signature will be required upon completion of intake.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pb-8">
          <Button onClick={handleSubmit} disabled={!isValid} className="gap-2">
            <ClipboardList className="h-4 w-4" /> Schedule Work Order
          </Button>
          <Button variant="outline" onClick={() => navigate('/work-orders')}>Cancel</Button>
        </div>
      </div>
    </>
  );
}
